import {FormControl} from '@angular/forms';
import {
  ImportModuleAdminEventGroup,
  ImportModuleBlankValues,
  ImportModuleDecisionRow,
  ImportModuleHistoryEventGroup,
  ImportModuleHistoryEventNamedParty,
  ImportModuleLookup,
  ImportModuleMappingEntry,
  ImportModuleMediaJoinSeveral,
  ImportModuleMediaMatch,
  ImportModuleMediaSetting,
  ImportModuleProfile,
  ImportModuleStoredDecision,
} from '../../core/definitions/import-module';
import {ObjectTypes} from '../../core/definitions/object-types';
import {
  AGENT_TYPE_ORGANIZATION,
  createGroupCardModel,
  createGroupMemberRow,
  createGroupSubBlockRow,
  defaultNamedTypeId,
  GroupCardDescriptor,
  GroupCardModel,
  GroupKind,
  GroupMemberRow,
  GroupSubBlockDef,
  GroupSubBlockRow,
  MemberSource,
  NamedObjectType,
} from './group-card/group-card.model';
import {CarriedProfile} from './profile-transfer';
import {formatAliases, hasLookupKeyOptions, LookupDefinition} from './lookup-controls';
import {ComposeLineRow} from './compose-field/compose-field.model';
import {
  applyPartsToTemplate,
  FlatValueSource,
  isFixedValueParts,
  restoreValueSource,
  ValueSourceRestoreContext,
} from './value-source';
import {
  decidedRow,
  DecisionSource,
  DEFAULT_MEDIA_MATCH,
  DEFAULT_MEDIA_MATCH_PRESET,
  GROUP_ID_KINDS,
  GroupIdKind,
  groupIdWireKey,
  mediaMatchPresetPattern,
  MediaOnMissing,
  PLACEMENT_EVENT_KIND,
  SessionSnapshot,
} from './import-payload';

/**
 * Restoring a saved profile or a resumed session onto the Import module
 * page, the inverse of the request-body builders in `import-payload.ts`.
 * `pendingRestore` reads what a profile or a session holds; once the
 * source's columns and target fields are known, `restoreProfile` rebuilds
 * the rows, cards and lookups from it, under fresh keys, and reports what
 * no longer resolves instead of dropping it.
 */

/** What a restore rebuilds once the source's columns are known. */
export interface PendingRestore {
  mapping: ImportModuleMappingEntry[];
  historyEventGroups: ImportModuleHistoryEventGroup[];
  groupIdGroups: Partial<Record<GroupIdKind, {key: string}[]>>;
  adminEventGroups: ImportModuleAdminEventGroup[];
  batch: { [key: string]: string };
  mediaJoinColumn: string | null;
  mediaOnMissing: MediaOnMissing;
  mediaMatch: ImportModuleMediaMatch;
  mediaJoinSeveral: ImportModuleMediaJoinSeveral;
  // The entries written onto the linked media (`media_mapping`).
  mediaMapping: ImportModuleMappingEntry[];
  // A saved profile's source table - a multi-table source selects it when
  // it has a table by that name.
  sourceTable?: string | null;
  blankValues?: ImportModuleBlankValues;
  // Values said to be real, per column; `{}` when none were kept.
  blankValuesRejected: ImportModuleBlankValues;
  lookups?: ImportModuleLookup[];
  // Set for a profile carried from a file: the ids blanked out of it, and
  // how many value decisions were dropped, which the page reports with the
  // restore's own warnings.
  carried?: Pick<CarriedProfile, 'blanked' | 'droppedDecisions'>;
}

/** A saved profile or a stashed session, whichever is being restored. */
export type RestoreSource = { profile: ImportModuleProfile } | { session: SessionSnapshot };

/** The pending restore of `source`. Only the keys named here are read:
 * anything else a stored profile or stash holds is left behind. */
export function pendingRestore(source: RestoreSource): PendingRestore {
  if ('profile' in source) {
    const profile = source.profile;
    return {
      mapping: profile.mapping,
      historyEventGroups: profile.history_event_groups,
      groupIdGroups: Object.fromEntries(GROUP_ID_KINDS.map(kind => [kind, profile[groupIdWireKey(kind)]])) as
        PendingRestore['groupIdGroups'],
      adminEventGroups: profile.admin_event_groups,
      batch: profile.batch,
      ...pendingMedia(profile.media),
      sourceTable: profile.source_table ?? null,
      blankValues: profile.blank_values,
      blankValuesRejected: profile.blank_values_rejected ?? {},
      lookups: profile.lookups,
    };
  }
  const session = source.session;
  return {
    mapping: session.mapping,
    historyEventGroups: session.historyEventGroups,
    groupIdGroups: session.groupIdGroups,
    adminEventGroups: session.adminEventGroups,
    batch: session.batch,
    ...pendingMedia(session.media),
    blankValues: session.blankValues,
    blankValuesRejected: session.blankValuesRejected ?? {},
    lookups: session.lookups,
  };
}

/** A saved media join; a key it lacks, the match rule's own included,
 * reads as its default. A pattern rule with a blank pattern reads as the
 * default preset's pattern. */
function pendingMedia(
  media: ImportModuleMediaSetting | null | undefined,
): Pick<PendingRestore, 'mediaJoinColumn' | 'mediaOnMissing' | 'mediaMatch' | 'mediaJoinSeveral' | 'mediaMapping'> {
  const match = media?.match;
  const mode = match?.mode === 'pattern' ? 'pattern' : DEFAULT_MEDIA_MATCH.mode;
  const pattern = match?.pattern ?? DEFAULT_MEDIA_MATCH.pattern;
  return {
    mediaJoinColumn: media?.join_column ?? null,
    mediaOnMissing: (media?.on_missing as MediaOnMissing) ?? 'block',
    mediaMatch: {
      mode,
      pattern: mode === 'pattern' && !pattern.trim() ? mediaMatchPresetPattern(DEFAULT_MEDIA_MATCH_PRESET)! : pattern,
      ignore_leading_zeros: match?.ignore_leading_zeros === true,
    },
    mediaJoinSeveral: media?.several === 'link_all' ? 'link_all' : 'skip',
    mediaMapping: media?.mapping ?? [],
  };
}

/** A saved profile's decisions as the rows a profile save sends: a
 * `pick_existing` binds its term through `resolved_artifact_id`. */
export function storedDecisionRows(decisions: ImportModuleStoredDecision[] | undefined): ImportModuleDecisionRow[] {
  return (decisions ?? []).map(decision => ({
    source_column: decision.source_column,
    source_value: decision.source_value,
    source_value_key: decision.source_value_key,
    decision_kind: decision.decision_kind,
    resolved_artifact_id: decision.target_artifact_id,
    new_concept_name: decision.new_concept_name,
    new_concept_code: decision.new_concept_code,
    new_concept_parent_id: decision.new_concept_parent_id,
  }));
}

/** The rows of `rows` that carry a decision, each as `decidedRow` binds it. */
export function decidedRows(rows: DecisionSource[]): ImportModuleDecisionRow[] {
  return rows.filter(row => row.decision_kind !== null).map(decidedRow);
}

/** A flat mapping row as a restore fills it in. */
export interface FlatRestoreRow<T> extends FlatValueSource {
  targetFieldControl: FormControl<string | T | null>;
  conceptRootControl: FormControl<string | null>;
}

/** What restoring lookups and group cards needs from the page. */
export interface RestorePage {
  descriptors: Record<GroupKind, GroupCardDescriptor>;
  // Every group kind in page order.
  groupKinds: readonly GroupKind[];
  columnNames: ReadonlySet<string>;
  // The tables a lookup can read.
  lookupTables: readonly string[];
  // Adds an empty lookup to the page, change tracking wired, and returns it.
  addLookup: () => LookupDefinition;
  nextGroupKey: () => string;
  nextSubBlockKey: () => number;
  nextComposeRowId: () => number;
}

/** Where restored flat rows go: the fields they can pick, and the page's
 * factory for a row. */
export interface FlatRestoreContext<T extends { field_id: string }, R extends FlatRestoreRow<T>> {
  // The target fields a flat row can pick.
  flatTargets: readonly T[];
  // Adds an empty flat row to the page, change tracking wired, and returns it.
  addFlatRow: () => R;
}

/** What `restoreProfile` needs from the page it restores onto. */
export interface ProfileRestoreContext<T extends { field_id: string }, R extends FlatRestoreRow<T>>
  extends RestorePage, FlatRestoreContext<T, R> {}

/** Something a restore could not put back: a translation key and its
 * parameters. */
export interface RestoreWarning {
  key: string;
  params?: any;
}

/** What `restoreProfile` leaves for the page to apply. */
export interface ProfileRestoreResult {
  // The values read as blank in the columns the source still has, trimmed.
  blankValues: ImportModuleBlankValues;
  // The media join, when the source still has its column.
  mediaJoin: {
    column: string; onMissing: MediaOnMissing; match: ImportModuleMediaMatch; several: ImportModuleMediaJoinSeveral;
  } | null;
  // The media join as restored, with the fields on the linked media, when
  // the source lacks its column: what a save keeps until the operator
  // picks a join column or turns media linking off.
  keptMedia: ImportModuleMediaSetting | null;
  // The restored group cards by kind. Not yet on the page, and their
  // controls not yet registered for change tracking.
  cards: Record<GroupKind, GroupCardModel[]>;
  // The flat rows' compose lines. The rows themselves are on the page,
  // these lines are not yet registered for change tracking.
  composeLines: ComposeLineRow[];
  warnings: RestoreWarning[];
}

type Warn = (key: string, params?: any) => void;

/**
 * Rebuild `pending` on the page described by `context`. Lookups and flat
 * rows go on the page through its factories as they are restored, the
 * entries onto the linked media through `mediaContext`, whose fields are
 * the media's; without one, no media field is known and each such entry
 * is reported. The values read as blank and the group cards are returned. Groups, sub-blocks and lookups get
 * fresh keys (a saved key could collide with one added by hand), and a
 * row naming a saved key finds its restored one. Whatever no longer
 * resolves (a missing column, target field, lookup table or sub-block) is
 * skipped and reported in `warnings`, each warning once.
 */
export function restoreProfile<T extends { field_id: string }, R extends FlatRestoreRow<T>>(
  pending: PendingRestore, context: ProfileRestoreContext<T, R>,
  mediaContext: FlatRestoreContext<T, R> = {flatTargets: [], addFlatRow: context.addFlatRow},
): ProfileRestoreResult {
  const warnings: RestoreWarning[] = [];
  const warned = new Set<string>();
  const warn: Warn = (key, params) => {
    const id = JSON.stringify([key, params ?? null]);
    if (!warned.has(id)) {
      warned.add(id);
      warnings.push({key, params});
    }
  };
  const cards = Object.fromEntries(
    context.groupKinds.map(kind => [kind, []])) as unknown as Record<GroupKind, GroupCardModel[]>;
  const composeLines: ComposeLineRow[] = [];

  let mediaJoin: ProfileRestoreResult['mediaJoin'] = null;
  let keptMedia: ProfileRestoreResult['keptMedia'] = null;
  if (pending.mediaJoinColumn) {
    if (context.columnNames.has(pending.mediaJoinColumn)) {
      mediaJoin = {
        column: pending.mediaJoinColumn, onMissing: pending.mediaOnMissing,
        match: pending.mediaMatch, several: pending.mediaJoinSeveral,
      };
    } else {
      warn('TRANS__IMPORT__PROFILE__RESTORE__COLUMN_MISSING', {column: pending.mediaJoinColumn});
      keptMedia = {
        join_column: pending.mediaJoinColumn, on_missing: pending.mediaOnMissing,
        match: pending.mediaMatch, several: pending.mediaJoinSeveral,
        mapping: pending.mediaMapping?.length ? pending.mediaMapping : undefined,
      };
    }
  }

  const blankValues: ImportModuleBlankValues = {};
  for (const [column, values] of Object.entries(pending.blankValues ?? {})) {
    if (!context.columnNames.has(column)) {
      warn('TRANS__IMPORT__PROFILE__RESTORE__COLUMN_MISSING', {column});
      continue;
    }
    const kept = values.map(value => value.trim()).filter(value => value);
    if (kept.length) {
      blankValues[column] = kept;
    }
  }

  // Before any row: a lookup row finds its lookup through this map.
  const lookupKeys = new Map(
    (pending.lookups ?? []).map(saved => [saved.key, restoreLookupDefinition(saved, context, warn).key]));
  const restorer = new CardRestorer(context, cards, lookupKeys, warn);

  const flatEntries = pending.mapping.filter(
    e => !e.group_id && !e.history_event_group && !e.admin_event_group);
  const historyEntries = pending.mapping.filter(e => !!e.history_event_group);
  const groupEntries = pending.mapping.filter(e => !!e.group_id);
  const adminEventEntries = pending.mapping.filter(e => !!e.admin_event_group);

  for (const entry of flatEntries) {
    restoreFlatAssignment(entry, context, restorer.valueSourceContext, composeLines, warn);
  }
  for (const entry of pending.mediaMapping ?? []) {
    restoreFlatAssignment(entry, mediaContext, restorer.valueSourceContext, composeLines, warn);
  }

  restorer.restoreHistoryEvents(pending.historyEventGroups, historyEntries);
  restorer.restoreGroupIdGroups(pending.groupIdGroups, groupEntries);
  restorer.restoreAdminEvents(pending.adminEventGroups, adminEventEntries);

  return {blankValues, mediaJoin, keptMedia, cards, composeLines, warnings};
}

/** Add a saved lookup back to the Kodeoppslag tab, under a fresh key. The
 * table goes first: its watch resets the key column and fetches the
 * table's columns. A table or code column the current source lacks is
 * left unpicked and reported. The key options open only when the lookup
 * carries one. */
function restoreLookupDefinition(
  saved: ImportModuleLookup, context: RestorePage, warn: Warn,
): LookupDefinition {
  const lookup = context.addLookup();
  lookup.nameControl.setValue(saved.name ?? '');
  if (context.lookupTables.includes(saved.table)) {
    lookup.tableControl.setValue(saved.table);
    lookup.keyColumnControl.setValue(saved.key_column);
  } else {
    warn('TRANS__IMPORT__PROFILE__RESTORE__LOOKUP_TABLE_MISSING', {table: saved.table});
  }
  if (context.columnNames.has(saved.code_column)) {
    lookup.codeColumnControl.setValue(saved.code_column);
  } else {
    warn('TRANS__IMPORT__PROFILE__RESTORE__COLUMN_MISSING', {column: saved.code_column});
  }
  applyPartsToTemplate(lookup.lookupKeyTemplateControl, saved.key_parts ?? [], context.columnNames, warn);
  lookup.lookupCaseInsensitiveControl.setValue(!!saved.case_insensitive);
  lookup.lookupFallbackSuffixControl.setValue(saved.fallback_suffix ?? '');
  lookup.lookupAliasesControl.setValue(formatAliases(saved.aliases ?? {}));
  lookup.lookupKeyOptionsControl.setValue(hasLookupKeyOptions(saved));
  return lookup;
}

/** Add a saved flat entry back as a row of `context`, collecting its
 * compose lines into `composeLines`. Literal text alone comes back as a
 * fixed value. An entry whose target field `context` no longer offers
 * adds no row and is reported. */
function restoreFlatAssignment<T extends { field_id: string }, R extends FlatRestoreRow<T>>(
  entry: ImportModuleMappingEntry, context: FlatRestoreContext<T, R>,
  valueSourceContext: ValueSourceRestoreContext, composeLines: ComposeLineRow[], warn: Warn,
): void {
  const target = context.flatTargets.find(f => f.field_id === entry.target_field);
  if (!target) {
    warn('TRANS__IMPORT__PROFILE__RESTORE__FIELD_MISSING', {field: entry.target_field});
    return;
  }
  const row = context.addFlatRow();
  row.targetFieldControl.setValue(target);
  // Picking the target resets the concept root, and the fetch of the
  // field's root terms never touches the control, so the saved root is set
  // after the target. The value stays even when the field's
  // roots don't load or no longer offer more than one.
  if (entry.concept_root_id) {
    row.conceptRootControl.setValue(entry.concept_root_id);
  }
  composeLines.push(...restoreValueSource(row, entry.parts, valueSourceContext));
  row.fixedValue = isFixedValueParts(entry.parts);
}

/** The group-card half of one restore: builds each card under a fresh key
 * into `cards`. */
class CardRestorer {
  readonly valueSourceContext: ValueSourceRestoreContext;

  constructor(
    private readonly context: RestorePage,
    private readonly cards: Record<GroupKind, GroupCardModel[]>,
    lookupKeys: ReadonlyMap<string, string>,
    private readonly warn: Warn,
  ) {
    this.valueSourceContext = {
      columnNames: context.columnNames,
      restoredLookupKey: savedKey => lookupKeys.get(savedKey) ?? null,
      nextComposeRowId: context.nextComposeRowId,
      warn,
    };
  }

  /** History-event cards: one per saved group, its Named and Place
   * sub-blocks, then the member entries of each. A sub-block entry finds
   * its sub-block through the saved `<group>.<sub-block>` key; the entries
   * of a sub-block skipped as unavailable are skipped with it. */
  restoreHistoryEvents(
    groups: ImportModuleHistoryEventGroup[] | undefined, entries: ImportModuleMappingEntry[],
  ): void {
    const descriptor = this.context.descriptors.history_event;
    const groupKeyMap = new Map<string, string>();
    const subBlockKeyMap = new Map<string, string>();
    const skippedSubBlocks = new Set<string>();
    const namedDef = descriptor.subBlocks.find(s => s.kind === 'named');
    const placeDef = descriptor.subBlocks.find(s => s.kind === 'place');
    const namedTargets: [GroupSubBlockRow, ImportModuleHistoryEventNamedParty][] = [];
    for (const g of groups ?? []) {
      const model = createGroupCardModel(this.context.nextGroupKey(), descriptor.header);
      groupKeyMap.set(g.key, model.key);
      if (g.event_type_id) {
        model.headerControls['event_type_id']?.setValue(g.event_type_id);
      }
      namedTargets.push(...this.restoreSubBlocks(
        model, g.key, g.named, namedDef, subBlockKeyMap, skippedSubBlocks,
        'TRANS__IMPORT__PROFILE__RESTORE__NAMED_UNAVAILABLE'));
      this.restoreSubBlocks(
        model, g.key, g.places, placeDef, subBlockKeyMap, skippedSubBlocks,
        'TRANS__IMPORT__PROFILE__RESTORE__PLACE_UNAVAILABLE');
      for (const entry of entries.filter(e => e.history_event_group === g.key && !e.sub_block)) {
        this.restoreGroupMember(model, descriptor, entry);
      }
      this.cards.history_event.push(model);
    }
    for (const entry of entries.filter(e => !!e.sub_block)) {
      const savedKey = `${entry.history_event_group}.${entry.sub_block}`;
      if (skippedSubBlocks.has(savedKey)) {
        continue;
      }
      const newKey = subBlockKeyMap.get(savedKey);
      const model = this.cards.history_event.find(m => m.subBlocks.some(s => s.key === newKey));
      const sub = model?.subBlocks.find(s => s.key === newKey);
      if (!sub) {
        this.warn('TRANS__IMPORT__PROFILE__RESTORE__NAMED_UNAVAILABLE');
        continue;
      }
      restoreMemberRow(sub.member, entry, true, this.valueSourceContext);
    }
    // After the members: an entry stored with the older keys reads them
    // by its member's value mode.
    for (const [row, entry] of namedTargets) {
      restoreNamedTarget(row, entry);
    }
    if (entries.some(e => !groupKeyMap.has(e.history_event_group!))) {
      this.warn('TRANS__IMPORT__PROFILE__RESTORE__GROUP_MISSING');
    }
  }

  /**
   * Cards of the kinds sharing the `group_id` wire key, one per `group_id`
   * in the entries. `group_parent` puts each target field in exactly one
   * of those kinds, so a group goes to the kind whose card offers one of
   * its entries' target fields. Reference is tried last, as the default
   * for a group that matches none of the others (an entry whose target
   * field belongs to a kind the page no longer has, say).
   */
  restoreGroupIdGroups(
    groups: PendingRestore['groupIdGroups'], entries: ImportModuleMappingEntry[],
  ): void {
    const byOldKey = (saved: {key: string}[] | undefined) =>
      new Map((saved ?? []).map(g => [g.key, g as unknown as Record<string, string>]));
    const storedByKind = Object.fromEntries(GROUP_ID_KINDS.map(
      kind => [kind, byOldKey(groups[kind])])) as Record<GroupIdKind, Map<string, Record<string, string>>>;
    const routingOrder = GROUP_ID_KINDS.filter(kind => kind !== 'reference');
    const entriesByKey = new Map<string, ImportModuleMappingEntry[]>();
    for (const entry of entries) {
      const bucket = entriesByKey.get(entry.group_id!) ?? [];
      bucket.push(entry);
      entriesByKey.set(entry.group_id!, bucket);
    }
    for (const [groupId, bucket] of entriesByKey) {
      const kind = routingOrder.find(candidate => bucket.some(
        e => this.context.descriptors[candidate].members.some(m => m.fieldId === e.target_field))) ?? 'reference';
      this.restoreGroupIdBucket(kind, groupId, bucket, storedByKind[kind]);
    }
  }

  /**
   * Admin-event and placement cards, which use their own wire key
   * (`admin_event_group`). A card per saved group first: a group with
   * header picks and no member column has no mapping entries at all, and
   * would otherwise be lost. Entries whose group was not saved (its header
   * was incomplete at save time) still get an admin-event card each, and
   * a warning. A `PLACEMENT_EVENT_KIND` group becomes a placement card; it
   * has no header, so it is always saved.
   */
  restoreAdminEvents(
    groups: ImportModuleAdminEventGroup[] | undefined, entries: ImportModuleMappingEntry[],
  ): void {
    const restoredKeys = new Set<string>();
    for (const g of groups ?? []) {
      const kind: GroupKind = g.admin_event_type === PLACEMENT_EVENT_KIND ? 'placement_event' : 'admin_event';
      const model = this.restoreGroup(
        this.context.descriptors[kind],
        entries.filter(e => e.admin_event_group === g.key),
        card => {
          card.headerControls['admin_event_sub_type_id']?.setValue(g.sub_type_id ?? null);
          card.headerControls['admin_event_purpose_type_id']?.setValue(g.purpose_type_id ?? null);
          card.onlyWhenMappedControl?.setValue(!!g.only_when_mapped);
        },
      );
      this.cards[kind].push(model);
      restoredKeys.add(g.key);
    }
    const leftoverByKey = new Map<string, ImportModuleMappingEntry[]>();
    for (const entry of entries) {
      if (restoredKeys.has(entry.admin_event_group!)) {
        continue;
      }
      const bucket = leftoverByKey.get(entry.admin_event_group!) ?? [];
      bucket.push(entry);
      leftoverByKey.set(entry.admin_event_group!, bucket);
    }
    for (const bucket of leftoverByKey.values()) {
      this.cards.admin_event.push(this.restoreGroup(
        this.context.descriptors.admin_event, bucket,
        () => this.warn('TRANS__IMPORT__PROFILE__RESTORE__GROUP_MISSING')));
    }
  }

  /** Sets a `group_id`-sharing kind's header controls from its stored
   * values, keyed by `descriptor.header[].key`. When the kind has required
   * header fields, all of them must be stored, or none is set and one
   * warning is reported. */
  private applyGroupIdHeader(
    descriptor: GroupCardDescriptor, model: GroupCardModel, stored: Record<string, string> | undefined,
  ): void {
    const requiredKeys = descriptor.header.filter(h => h.required).map(h => h.key);
    if (requiredKeys.length && !requiredKeys.every(key => !!stored?.[key])) {
      this.warn('TRANS__IMPORT__PROFILE__RESTORE__GROUP_MISSING');
      return;
    }
    for (const h of descriptor.header) {
      const value = stored?.[h.key];
      if (value) {
        model.headerControls[h.key]?.setValue(value);
      }
    }
  }

  /** Restores one `group_id` group as a card of `kind`. */
  private restoreGroupIdBucket(
    kind: GroupIdKind, groupId: string, entries: ImportModuleMappingEntry[],
    storedByOldKey: Map<string, Record<string, string>>,
  ): void {
    const descriptor = this.context.descriptors[kind];
    const stored = storedByOldKey.get(groupId);
    const model = this.restoreGroup(descriptor, entries, card => {
      this.applyGroupIdHeader(descriptor, card, stored);
      // `stored` is the group's wire entry; its option values are not
      // strings the way its header values are.
      const options = stored as {create_missing?: boolean; create_missing_parent_id?: string} | undefined;
      card.createMissingControl?.setValue(!!options?.create_missing);
      card.createMissingParentControl?.setValue(options?.create_missing_parent_id ?? null);
    });
    this.cards[kind].push(model);
  }

  /** One restored card under a fresh key: `applyHeader` sets its
   * group-level config, then every member row is restored. */
  private restoreGroup(
    descriptor: GroupCardDescriptor, entries: ImportModuleMappingEntry[],
    applyHeader: (model: GroupCardModel) => void,
  ): GroupCardModel {
    const model = createGroupCardModel(
      this.context.nextGroupKey(), descriptor.header, !!descriptor.withOnlyWhenMappedToggle,
      !!descriptor.supportsCreateMissing);
    applyHeader(model);
    for (const entry of entries) {
      this.restoreGroupMember(model, descriptor, entry);
    }
    return model;
  }

  /** Restore one group's sub-block list (`named` or `places`) onto
   * `model`, recording each saved `<group>.<sub-block>` key's new row key
   * in `keyMap`. Without `def` (the sub-block's target field is no longer
   * mappable) each saved sub-block is reported, skipped and its saved key
   * recorded in `skipped`. Returns each
   * restored row with its saved entry, for `restoreNamedTarget` once the
   * row's member is back. */
  private restoreSubBlocks(
    model: GroupCardModel, oldGroupKey: string, entries: ImportModuleHistoryEventNamedParty[] | undefined,
    def: GroupSubBlockDef | undefined, keyMap: Map<string, string>, skipped: Set<string>,
    unavailableWarnKey: string,
  ): [GroupSubBlockRow, ImportModuleHistoryEventNamedParty][] {
    const restored: [GroupSubBlockRow, ImportModuleHistoryEventNamedParty][] = [];
    for (const entry of entries ?? []) {
      if (!def) {
        this.warn(unavailableWarnKey);
        skipped.add(`${oldGroupKey}.${entry.key}`);
        continue;
      }
      const row = createGroupSubBlockRow(`${model.key}.${this.context.nextSubBlockKey()}`, def);
      keyMap.set(`${oldGroupKey}.${entry.key}`, row.key);
      if (entry.role_id) {
        row.headerControls['role_id']?.setValue(entry.role_id);
      }
      if (entry.role_status_id) {
        row.headerControls['role_status_id']?.setValue(entry.role_status_id);
      }
      model.subBlocks.push(row);
      restored.push([row, entry]);
    }
    return restored;
  }

  private restoreGroupMember(
    model: GroupCardModel, descriptor: GroupCardDescriptor, entry: ImportModuleMappingEntry,
  ): void {
    const def = descriptor.members.find(m => m.fieldId === entry.target_field);
    if (!def) {
      this.warn('TRANS__IMPORT__PROFILE__RESTORE__FIELD_MISSING', {field: entry.target_field});
      return;
    }
    // A repeatable field (comments) gets its own fresh row per entry -
    // reusing an existing one the way every other field does here would
    // silently collapse several saved comments back down to one.
    let row = def.repeatable ? undefined : model.members.find(r => r.fieldId === entry.target_field);
    if (!row) {
      row = createGroupMemberRow(entry.target_field);
      model.members.push(row);
    }
    restoreMemberRow(row, entry, def.concept, this.valueSourceContext);
  }
}

/**
 * Put a saved Named entry's object type and type back on `row`'s target
 * (none on a Place row). An entry with `object_type` or `type_id` is read
 * by them, and an Agent with no type is a Person.
 *
 * An entry with neither is read by its older keys, as the backend reads
 * them: `kulturnav_target_object_type` is the object type, and
 * `agent_type: "organization"` the Organisasjon agent type. Each of those
 * applied to one value mode only - the object type to KulturNav uuids,
 * the agent type to names - so a row whose member is not in KulturNav-UUID
 * mode is an Agent, and a NamedObject takes no agent type.
 */
function restoreNamedTarget(row: GroupSubBlockRow, entry: ImportModuleHistoryEventNamedParty): void {
  if (!row.target) {
    return;
  }
  let objectType: NamedObjectType;
  let typeId: string | null;
  if (entry.object_type || entry.type_id) {
    objectType = entry.object_type === ObjectTypes.NAMED_OBJECT ? ObjectTypes.NAMED_OBJECT : ObjectTypes.AGENT;
    typeId = entry.type_id || defaultNamedTypeId(objectType);
  } else {
    const kulturNavUuid = row.member.valueTypeControl.value === 'kulturnav_uuid';
    objectType = kulturNavUuid && entry.kulturnav_target_object_type === ObjectTypes.NAMED_OBJECT
      ? ObjectTypes.NAMED_OBJECT : ObjectTypes.AGENT;
    typeId = objectType === ObjectTypes.AGENT && entry.agent_type === 'organization'
      ? AGENT_TYPE_ORGANIZATION : defaultNamedTypeId(objectType);
  }
  row.target.objectTypeControl.setValue(objectType);
  row.target.typeIdControl.setValue(typeId);
}

/** Put a saved member entry back on `row`. */
function restoreMemberRow(
  row: GroupMemberRow, entry: ImportModuleMappingEntry, concept: boolean,
  valueSourceContext: ValueSourceRestoreContext,
): void {
  const source: MemberSource = entry.member_source
    ?? (entry.parts.some(p => p.kind !== 'text') ? 'column' : 'constant');
  row.sourceControl.setValue(source);
  const kulturNavUuid = concept && !!entry.value_is_kulturnav_uuid;
  if (kulturNavUuid) {
    row.valueTypeControl.setValue('kulturnav_uuid');
  }
  if (source === 'column') {
    restoreValueSource(row, entry.parts, valueSourceContext);
    return;
  }
  const value = entry.parts[0]?.value ?? '';
  if (concept && !kulturNavUuid) {
    row.conceptControl.setValue(value);
  } else {
    row.constantControl.setValue(value);
  }
}
